import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Col } from "reactstrap";
import { useQuery, useApolloClient, useMutation } from "@apollo/client";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { duplicateResponse } from "../Helpers/functions";
import { ProjectContext } from "../ProjectProvider";
import {
  prepareOrgVersion,
  filterOrgVersionList,
  getCategoryColumn,
} from "./functions";
import { PageWrapper } from "../PageWrapper";
import {
  CONSOLIDATE_ORG_VERSION,
  SNAPSHOT_ORG_VERSION,
} from "../GraphQl/mutation";
import { MANAGERPAGE, ORGVERSION } from "../../navigation/Routes";
import { CONSOLIDATED, SUBMITTED } from "../Helpers/constants";
import GenericListContainer from "../GenericList/GenericListContainer";
import {
  FIND_PROJECT,
  GET_PRJ_VERSION_INFO,
  ORG_VERSION_MANAGER,
} from "../GraphQl/query";
import AlertComponent from "../ModelItemsForm/AlertComponent";

const OrgVersionContainer = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [isAlertOpen, setAlertOpen] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setProject, projectData } = useContext(ProjectContext);
  const { state } = useLocation();
  const { projectVersionId } = useParams();
  const client = useApolloClient();

  useEffect(() => setProject(""), [setProject]);

  const { data: p_dataProject } = useQuery(FIND_PROJECT, {
    variables: { projectVersionId },
  });

  const { data, loading, error } = useQuery(ORG_VERSION_MANAGER, {
    fetchPolicy: "network-only",
    variables: { projVersion: parseInt(projectVersionId) },
  });

  const { data: prVersionInfo } = useQuery(GET_PRJ_VERSION_INFO);

  const { projectVersionDesc, hasUnits } = state ? state : prVersionInfo;

  const [consolidate] = useMutation(CONSOLIDATE_ORG_VERSION, {
    onCompleted(mutationData) {
      if (mutationData.result) {
        setIsLoading(false);
        navigate(MANAGERPAGE, { refetch: true });
      }
    },
  });

  const [snapshot] = useMutation(SNAPSHOT_ORG_VERSION, {
    onCompleted(data) {
      if (data.result) {
        setIsLoading(false);
        navigate(`${ORGVERSION}/${projectVersionId}`);
      }
    },
  });

  const handleConsolidate = async () => {
    try {
      setIsLoading(true);
      await consolidate({
        variables: {
          projectVersionId: projectVersionId,
        },
      });
    } catch (e) {
      setIsLoading(false);
      setAlertMessage(t("GENERIC_ERROR")); // needs to use apollo error link
      setAlertOpen(true);
    }
  };

  const handleSnapshot = async () => {
    try {
      setIsLoading(true);
      await snapshot({
        variables: {
          projectVersionId: projectVersionId,
        },
      });
    } catch (e) {
      setIsLoading(false);
      setAlertOpen(true);
      setAlertMessage(t("GENERIC_ERROR")); // needs to use apollo error link
    }
  };

  let missingheader;

  if (
    p_dataProject !== undefined &&
    Object.hasOwn(p_dataProject, "result") &&
    Object.hasOwn(p_dataProject.result, "code") &&
    Object.hasOwn(p_dataProject.result, "instituteDescription")
  ) {
    missingheader =
      p_dataProject.result.code +
      "\n" +
      p_dataProject.result.instituteDescription;
  }

  if (state?.from) {
    client.writeQuery({
      query: GET_PRJ_VERSION_INFO,
      data: {
        projectVersionDesc: projectVersionDesc,
        hasUnits: hasUnits,
      },
    });
  }
  const list = duplicateResponse(data?.organizationVersionsManager);
  const orgVersionList = prepareOrgVersion(list);
  const isProjectVersionActive = orgVersionList?.[0]?.version?.active || false;

  const sortedOrgVersionList = orgVersionList?.sort(function (a, b) {
    if (a.organization < b.organization) {
      return -1;
    }
    if (a.organization > b.organization) {
      return 1;
    }
    return 0;
  });

  const additionalColumns = [getCategoryColumn(t("Categorie"), navigate)];

  const filteredByDateList = filterOrgVersionList(sortedOrgVersionList);
  if (filteredByDateList?.length) {
    missingheader =
      filteredByDateList[0].code +
      "\n" +
      filteredByDateList[0].instituteDescription;
  }

  const isConsolidatedDisabled = filteredByDateList.some(
    (item) => item.status === SUBMITTED || item.status === CONSOLIDATED,
  );

  const TableHeader = hasUnits && isProjectVersionActive && (
    <>
      <Col xs="2">
        <Button
          disabled={isLoading || isConsolidatedDisabled}
          color="danger"
          onClick={handleConsolidate}
        >
          {t("cons")}
        </Button>
      </Col>
      <Col xs="2">
        <Button disabled={isLoading} color="warning" onClick={handleSnapshot}>
          {t("snapshot")}
        </Button>
      </Col>
    </>
  );

  return (
    <PageWrapper loading={loading} error={error}>
      {isAlertOpen && (
        <AlertComponent
          color={"danger"}
          isAlertOpen={isAlertOpen}
          message={alertMessage}
          toggleAlert={setAlertOpen}
        />
      )}
      <GenericListContainer
        list={filteredByDateList}
        isProjectVersionActive={isProjectVersionActive}
        additionalColumns={isProjectVersionActive ? additionalColumns : null}
        structureName={"OrganizationVersion"}
        parentValue={projectVersionDesc}
        isManagerHasUnit={hasUnits}
        missingheader={missingheader}
        TableHeader={TableHeader}
        loadingStatus={isLoading}
        projectDates={{
          projectCode: projectData?.code,
          versionEndingDate: projectData?.endingDate,
          versionStartingDate: projectData?.startingDate,
          projectEndingDate: projectData?.projectEndingDate,
          projectStartingDate: projectData?.projectStartingDate,
          projectExtensionDate: projectData?.projectExtensionDate,
        }}
      />
    </PageWrapper>
  );
};

export default OrgVersionContainer;
